import { Meta } from "@/@types/paginated";
import { Button } from "@/components/ui/button";
import { DEFAULT_PAGE, PAGINATION_LABELS } from "@/constants/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PER_PAGE_OPTIONS } from "../constants";
import { useSectorEmailsFilterActions } from "../filter/hooks/actions";
import { useSectorEmailsFilters } from "../filter/hooks/filterState";
import { SECTOR_EMAILS_LABELS } from "../messages";

interface DataTablePaginationProps {
  meta: Meta;
}

export function DataTablePagination({
  meta,
}: DataTablePaginationProps): JSX.Element {
  const { setPagination } = useSectorEmailsFilterActions();
  const { meta: pagination } = useSectorEmailsFilters();

  const lastPage: number = Math.max(meta.lastPage, DEFAULT_PAGE);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-2">
      <div className="flex-1 text-sm text-muted-foreground">
        {SECTOR_EMAILS_LABELS.RESULT_COUNT(meta.total)}
      </div>
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">
            {PAGINATION_LABELS.ROWS_PER_PAGE}
          </p>
          <Select
            value={`${pagination.perPage}`}
            onValueChange={(value: string) =>
              setPagination({ page: DEFAULT_PAGE, perPage: Number(value) })
            }
          >
            <SelectTrigger
              className="h-8 w-[70px]"
              aria-label={PAGINATION_LABELS.ROWS_PER_PAGE}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent side="top">
              {PER_PAGE_OPTIONS.map((pageSize: number) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-sm font-medium">
          {SECTOR_EMAILS_LABELS.PAGE_OF(pagination.page, lastPage)}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            aria-label={PAGINATION_LABELS.PREVIOUS}
            onClick={() =>
              setPagination({
                page: pagination.page - 1,
                perPage: pagination.perPage,
              })
            }
            disabled={pagination.page <= DEFAULT_PAGE}
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            aria-label={PAGINATION_LABELS.NEXT}
            onClick={() =>
              setPagination({
                page: pagination.page + 1,
                perPage: pagination.perPage,
              })
            }
            disabled={pagination.page >= lastPage}
          >
            <ChevronRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
